import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  Slide,
  Skeleton,
  Avatar,
  Divider,
  ThemeProvider,
  createTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { api } from '../context/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';

// Create a memorial-specific theme with gray and white
const memorialTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#616161', // Medium gray 
      light: '#9e9e9e',
      dark: '#424242',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#757575', // Darker gray
      light: '#a4a4a4',
      dark: '#494949',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#f9f9f9',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

// Transition for the full screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Hardcoded backend URL to ensure consistent access
const BACKEND_URL = 'https://memorylink.onrender.com';

const QRMemorialViewer = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  // State
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [useDataUrls, setUseDataUrls] = useState(false);
  const [dataUrls, setDataUrls] = useState({});
  
  // Photo viewer states
  const [openPhotoViewer, setOpenPhotoViewer] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  console.log('QRMemorialViewer using backend URL:', BACKEND_URL);
  console.log('QR Code from URL:', code);

  const goBack = () => {
    navigate(-1);
  };

  const logError = useCallback((type, error, details) => {
    console.error(`[${type}] Error:`, {
      error,
      details,
      timestamp: new Date().toISOString(),
      qrCode: code
    });
  }, [code]);

  // Format date helper function
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (e) {
      return dateString || 'Unknown';
    }
  };

  // Ensure URLs are absolute
  const ensureAbsoluteUrl = useCallback((url) => {
    if (!url) return '';
    
    // If it's already an absolute URL
    if (url.startsWith('http')) {
      return url;
    }
    
    // Make sure the path starts with a slash
    const path = url.startsWith('/') ? url : `/${url}`;
    
    // Use the hardcoded URL
    return `${BACKEND_URL}${path}`;
  }, []);
  
  // Function to get base64 data URL from filename
  const getBase64DataUrl = useCallback(async (filename) => {
    try {
      // Extract filename from full URL if needed
      const justFilename = filename.includes('/uploads/') 
        ? filename.split('/uploads/')[1] 
        : filename.startsWith('/') 
          ? filename.substring(1) 
          : filename;
          
      // If we already have this cached, return it
      if (dataUrls[justFilename]) {
        return dataUrls[justFilename];
      }
      
      console.log('Fetching base64 data for:', justFilename);
      const response = await api.get(`/api/base64-image/${justFilename}`);
      
      if (response.data && response.data.success && response.data.dataUrl) {
        // Cache this data URL
        setDataUrls(prev => ({
          ...prev,
          [justFilename]: response.data.dataUrl
        }));
        return response.data.dataUrl;
      }
      throw new Error('Failed to get base64 data');
    } catch (error) {
      console.error('Base64 fetch error:', error);
      return null;
    }
  }, [dataUrls]);

  const preloadImages = useCallback(async (imageUrls) => {
    try {
      console.log('Preloading images:', imageUrls);
      
      // Normal image preloading
      const preloadPromises = imageUrls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            console.log(`Preloaded: ${url}`);
            resolve(true);
          };
          img.onerror = (e) => {
            console.error(`Failed to preload: ${url}`, e);
            reject(new Error(`Failed to load: ${url}`));
          };
          img.src = url;
        });
      });

      const results = await Promise.allSettled(preloadPromises);
      const allFailed = results.every(result => result.status === 'rejected');
      
      // If all images failed to load, try to use base64 data URLs
      if (allFailed) {
        console.log('All image preloads failed, switching to data URLs');
        setUseDataUrls(true);
        
        // Start fetching base64 data for all images
        const filenames = imageUrls.map(url => {
          if (url.includes('/uploads/')) {
            return url.split('/uploads/')[1];
          }
          return url.startsWith('/') ? url.substring(1) : url;
        });
        
        // Preload all base64 data
        const base64Promises = filenames.map(filename => getBase64DataUrl(filename));
        await Promise.allSettled(base64Promises);
      }
      
      setImagesPreloaded(true);
    } catch (error) {
      logError('Preload', error, { imageUrls });
      setImagesPreloaded(true); // Continue anyway
    }
  }, [logError, getBase64DataUrl]);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        if (!code) {
          throw new Error('No QR code provided');
        }
        
        setLoading(true);
        console.log(`Fetching memorial with QR code: ${code}`);
        
        // Changed this to use the QR code endpoint instead of ID
        const response = await api.get(`/api/memorials/qr/${code}`);
        console.log('API response:', response);
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to load memorial');
        }
        
        let memorialData;
        if (response.data && response.data.memorial) {
          memorialData = response.data.memorial;
        } else {
          throw new Error('Invalid response format from server');
        }
        
        // Process photo URLs to ensure absolute paths
        if (memorialData.photos && memorialData.photos.length > 0) {
          console.log('Original photo URLs:', memorialData.photos.map(p => p.url));
          
          memorialData.photos = memorialData.photos.map(photo => ({
            ...photo,
            url: ensureAbsoluteUrl(photo.url)
          }));
          
          console.log('Processed photo URLs:', memorialData.photos.map(p => p.url));
        }
        
        setMemorial(memorialData);
        setPhotos(memorialData.photos || []);

        if (memorialData.photos?.length > 0) {
          const imageUrls = memorialData.photos.map(photo => photo.url);
          await preloadImages(imageUrls);
        } else {
          setImagesPreloaded(true);
        }
      } catch (err) {
        logError('Fetch', err, { endpoint: `/api/memorials/qr/${code}` });
        setError(`Failed to load memorial: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchMemorial();
    } else {
      setError('No QR code provided');
      setLoading(false);
    }
  }, [code, logError, preloadImages, ensureAbsoluteUrl]);

  // Handle opening the photo viewer
  const handleOpenPhotoViewer = (index) => {
    setCurrentPhotoIndex(index);
    setOpenPhotoViewer(true);
  };

  // Handle closing the photo viewer
  const handleClosePhotoViewer = () => {
    setOpenPhotoViewer(false);
  };

  // Navigate to the next photo
  const handleNextPhoto = (event) => {
    event.stopPropagation();
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  // Navigate to the previous photo
  const handlePrevPhoto = (event) => {
    event.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  // Get profile photo (first photo or placeholder)
  const profilePhoto = photos && photos.length > 0 ? photos[0].url : 'https://via.placeholder.com/300';

  // Photo Gallery Grid Component
  const PhotoGallery = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'grey.800' }}>Photos</Typography>
      
      {!imagesPreloaded ? (
        // Skeleton loading state for photos
        <Grid container spacing={1}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={4} sm={4} key={index}>
              <Skeleton 
                variant="rectangular" 
                sx={{ 
                  height: 0,
                  paddingTop: '100%',
                  borderRadius: 1,
                  bgcolor: 'grey.200'
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : photos && photos.length > 0 ? (
        <Grid container spacing={1}>
          {photos.map((photo, index) => {
            // Get the proper image source (URL or data URL)
            const imageUrl = useDataUrls && dataUrls[photo.url.split('/uploads/')[1]] 
              ? dataUrls[photo.url.split('/uploads/')[1]] 
              : photo.url;
            
            return (
              <Grid item xs={4} sm={4} key={index}>
                <ErrorBoundary type="image">
                  <Box 
                    sx={{ 
                      height: 0, 
                      paddingTop: '100%', 
                      position: 'relative',
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.07)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                        '& .MuiBox-root': {
                          opacity: 0.9,
                        }
                      }
                    }}
                    onClick={() => handleOpenPhotoViewer(index)}
                  >
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '100%' 
                    }}>
                      <img
                        src={imageUrl}
                        alt={photo.description || `Photo ${index + 1}`}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', imageUrl);
                          // If we're not already using data URLs, try to get one for this image
                          if (!useDataUrls) {
                            const filename = photo.url.split('/uploads/')[1] || 
                                           (photo.url.startsWith('/') ? photo.url.substring(1) : photo.url);
                            getBase64DataUrl(filename).then(dataUrl => {
                              if (dataUrl) {
                                e.target.src = dataUrl;
                              }
                            });
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </ErrorBoundary>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          bgcolor: 'grey.100',
          borderRadius: 2
        }}>
          <Typography variant="body1" color="text.secondary">
            No photos available
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Actual component rendering starts here
  return (
    <ThemeProvider theme={memorialTheme}>
      <ErrorBoundary fallback="Error loading memorial page">
        {/* Loading state */}
        {loading && (
          <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ color: 'grey.700' }} />
            <Typography sx={{ mt: 2, color: 'grey.700' }}>Loading memorial...</Typography>
          </Container>
        )}
        
        {/* Error state */}
        {error && (
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Alert severity="error">{error}</Alert>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <IconButton onClick={goBack}>
                <ArrowBackIcon />
                <Typography sx={{ ml: 1 }}>Go Back</Typography>
              </IconButton>
            </Box>
          </Container>
        )}
        
        {/* Memorial not found state */}
        {!loading && !error && !memorial && (
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Alert severity="warning">Memorial not found</Alert>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <IconButton onClick={goBack}>
                <ArrowBackIcon />
                <Typography sx={{ ml: 1 }}>Go Back</Typography>
              </IconButton>
            </Box>
          </Container>
        )}
        
        {/* Memorial content */}
        {!loading && !error && memorial && (
          <Container 
            maxWidth="md" 
            sx={{ 
              py: 4, 
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <IconButton 
                onClick={goBack} 
                size="large" 
                sx={{ 
                  color: 'grey.700',
                  bgcolor: 'grey.100',
                  '&:hover': {
                    bgcolor: 'grey.200'
                  },
                  borderRadius: 2,
                  transition: 'all 0.2s ease'
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>

            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                border: '1px solid',
                borderColor: 'grey.100'
              }}
            >
              {/* Profile Header with ENLARGED Photo and Info */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                mb: 4 
              }}>
                {/* ENLARGED Avatar/Profile Image */}
                <Avatar 
                  src={profilePhoto} 
                  alt={memorial.name}
                  sx={{ 
                    width: { xs: 200, sm: 280 }, 
                    height: { xs: 200, sm: 280 }, 
                    border: '5px solid #f5f5f5',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    mb: 3,
                  }}
                />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      color: 'grey.900', 
                      fontSize: { xs: '1.75rem', sm: '2.125rem' }
                    }}
                  >
                    {memorial.name}
                  </Typography>
                  
                  <Typography 
                    variant="subtitle1" 
                    sx={{ mb: 1, color: 'grey.600' }}
                  >
                    {memorial.dateOfBirth && memorial.dateOfDeath ? (
                      <>
                        {formatDate(memorial.dateOfBirth)} - {formatDate(memorial.dateOfDeath)}
                      </>
                    ) : null}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', mt: 2, justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" color="grey.800">{photos.length}</Typography>
                      <Typography variant="body2" color="grey.500">Photos</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3, borderColor: 'grey.200' }} />

              {/* Memorial Content */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    lineHeight: 1.8,
                    color: 'grey.700'
                  }}
                >
                  {memorial.biography}
                </Typography>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'grey.200' }} />

              {/* Photos Gallery */}
              <PhotoGallery />
            </Paper>
          </Container>
        )}

        {/* Full Screen Photo Viewer Dialog */}
        <Dialog
          fullScreen
          open={openPhotoViewer}
          onClose={handleClosePhotoViewer}
          TransitionComponent={Transition}
          sx={{ 
            '& .MuiDialog-paper': { 
              bgcolor: 'rgba(0, 0, 0, 0.95)',
              overflowY: 'hidden'
            }
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16, 
            zIndex: 10 
          }}>
            <IconButton 
              onClick={handleClosePhotoViewer} 
              sx={{ color: 'white' }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <DialogContent 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 0,
              height: '100%',
              position: 'relative',
              userSelect: 'none',
              touchAction: 'pan-y'
            }}
          >
            {photos.length > 0 && (
              <>
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 1, sm: 2 },
                    zIndex: 1
                  }}
                >
                  <IconButton 
                    onClick={handlePrevPhoto} 
                    sx={{ 
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.3)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                      visibility: currentPhotoIndex === 0 ? 'hidden' : 'visible',
                      transition: 'all 0.2s ease'
                    }}
                    size="large"
                  >
                    <NavigateBeforeIcon fontSize="large" />
                  </IconButton>
                  <IconButton 
                    onClick={handleNextPhoto} 
                    sx={{ 
                      color: 'white', 
                      bgcolor: 'rgba(0,0,0,0.3)', 
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                      visibility: currentPhotoIndex === photos.length - 1 ? 'hidden' : 'visible',
                      transition: 'all 0.2s ease'
                    }}
                    size="large"
                  >
                    <NavigateNextIcon fontSize="large" />
                  </IconButton>
                </Box>
                
                <ErrorBoundary type="image">
                  <Box
                    sx={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Use direct img tag for fullscreen view */}
                    <img
                      src={
                        useDataUrls && dataUrls[photos[currentPhotoIndex].url.split('/uploads/')[1]]
                          ? dataUrls[photos[currentPhotoIndex].url.split('/uploads/')[1]]
                          : photos[currentPhotoIndex].url
                      }
                      alt={photos[currentPhotoIndex].description || `Photo ${currentPhotoIndex + 1}`}
                      style={{
                        maxHeight: '90vh',
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        console.error('Fullscreen image failed to load:', photos[currentPhotoIndex].url);
                        // Try to load base64 data URL as fallback
                        const filename = photos[currentPhotoIndex].url.split('/uploads/')[1] || 
                                      (photos[currentPhotoIndex].url.startsWith('/') 
                                          ? photos[currentPhotoIndex].url.substring(1) 
                                          : photos[currentPhotoIndex].url);
                        getBase64DataUrl(filename).then(dataUrl => {
                          if (dataUrl) {
                            e.target.src = dataUrl;
                          }
                        });
                      }}
                    />
                  </Box>
                </ErrorBoundary>
                
                {/* Caption */}
                {photos[currentPhotoIndex].description && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0,
                      p: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      textAlign: 'center',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <Typography variant="body1">
                      {photos[currentPhotoIndex].description}
                    </Typography>
                  </Box>
                )}
                
                {/* Photo Counter */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 16, 
                    left: 16,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    borderRadius: 10,
                    px: 1.5,
                    py: 0.5,
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <Typography variant="body2">
                    {currentPhotoIndex + 1} / {photos.length}
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default QRMemorialViewer;